import { lazy, useEffect, useState } from "react";
import { HubCapsule } from "@aws-amplify/core";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { getInstanceOfAuthenticationService } from "src/dependencies";
import { AuthenticationService } from "src/services";
import { ROUTES_CONFIG, BRAND_ROUTES_CONFIG } from "src/constants/routesConfig";
import { Wrapper, WrapperProps } from "src/types/wrapper";
import { AuthenticationContext } from "src/context";

const PUBLIC_ROUTE_PATHS = Object.values(BRAND_ROUTES_CONFIG)
  .filter((r): r is typeof r & { isPublic: true } => !!(r as { isPublic?: boolean }).isPublic)
  .map((r) => r.path);

const AuthComponent = lazy(() => import("src/pages/general/auth"));

interface AuthenticationProviderProps extends WrapperProps {
  authenticationService: AuthenticationService;
}

const AuthenticationProvider: Wrapper<AuthenticationProviderProps> = ({ children, authenticationService }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();

  const isPublicRoute = PUBLIC_ROUTE_PATHS.some((path) => matchPath(path, location.pathname));
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const fetchUserEmail = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      setUserEmail(user.attributes.email || null);
    } catch (error) {
      console.error("Failed to fetch user email:", error);
      setUserEmail(null);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const currentSession = await authenticationService.getCurrentSession();
      const isAuth = !!currentSession;
      setIsAuthenticated(isAuth);

      if (isAuth) {
        await fetchUserEmail();
      } else {
        setUserEmail(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUserEmail(null);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationService]);

  useEffect(() => {
    const onAuthEvent = async (capsule: HubCapsule) => {
      const { event } = capsule.payload;

      if (event === "signIn") {
        await checkAuthStatus();
        navigate(ROUTES_CONFIG.home.path);
      }

      if (event === "signOut") {
        setIsAuthenticated(false);
      }
    };

    authenticationService.subscribeToAuthEvents(onAuthEvent);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationService, navigate]);

  const render = () => {
    if (isPublicRoute) {
      return children;
    }

    if (isAuthenticated === undefined) {
      return null;
    }

    if (isAuthenticated) {
      return children;
    }

    return <AuthComponent />;
  };

  return (
    <AuthenticationContext.Provider value={{ logout: () => authenticationService.handleSignOut(), userEmail }}>
      {render()}
    </AuthenticationContext.Provider>
  );
};

// DI
export default (props: Omit<AuthenticationProviderProps, "authenticationService">) => (
  <AuthenticationProvider authenticationService={getInstanceOfAuthenticationService()} {...props} />
);
